import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { JdcManagerService } from '../jdc-manager.service';
import { GtwSnackbarService } from '../../../../shared/_services/gtw-snackbar.service';

@Component({
    selector: 'gtw-jdc-copy-roles-access-modal',
    templateUrl: './jdc-copy-roles-access-modal.component.html',
    styleUrls: ['./jdc-copy-roles-access-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class JdcCopyRolesAccessModalComponent implements OnInit, OnDestroy {
  @Input() treeNodes: any[] = [];
  @Input() jcdKey: any;
  @Input() clientKey: any;
  @Input() globalParams: any;
  @Input() targetActivity: any;

  @Output() saved = new EventEmitter<void>();
  @Output() closed = new EventEmitter<void>();

  sourceActivityName = '';
  targetActivityName = '';
  selectedSourceActivityKey: any = null;
  selectedTargetActivityKey: any = null;
  sourceRolesList: any[] = [];
  targetRolesList: any[] = [];
  selectedSourceRole = '';
  selectedTargetRole = '';
  mappedRolesList: any[] = [];
  allActivitiesForModal: any[] = [];
  sourceTree: any[] = [];
  saveMappingLoader = false;

  private destroy$ = new Subject<void>();

  constructor(
    private jdcManagerService: JdcManagerService,
    private gtwSnackbarService: GtwSnackbarService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    const activity = this.targetActivity;
    this.targetActivityName = activity?.activity_name || '';
    this.selectedTargetActivityKey = activity?.activity_key;
    this.targetRolesList = (activity?.roles || []).map((r: any) => (typeof r === 'string' ? r : r?.role_name)).filter((r: any) => !!r);
    this.selectedTargetRole = this.targetRolesList[0] || '';
    this.mappedRolesList = [];
    this.allActivitiesForModal = this.listAllActivities(this.treeNodes).filter((a: any) => a?.activity_key !== this.selectedTargetActivityKey);
    this.sourceTree = this.buildSourceTree(this.treeNodes);
    if (this.allActivitiesForModal.length > 0) {
      this.selectSourceActivity(this.allActivitiesForModal[0]);
    } else {
      this.sourceRolesList = [];
      this.selectedSourceRole = '';
      this.sourceActivityName = '';
      this.selectedSourceActivityKey = null;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  close(): void {
    this.closed.emit();
  }

  selectSourceActivity(activity: any): void {
    this.selectedSourceActivityKey = activity?.activity_key;
    this.sourceActivityName = activity?.activity_name || '';
    this.sourceRolesList = (activity?.roles || []).map((r: any) => (typeof r === 'string' ? r : r?.role_name)).filter((r: any) => !!r);
    this.selectedSourceRole = this.sourceRolesList[0] || '';
    this.mappedRolesList = [];
    this.cdr.detectChanges();
  }

  addRoleMapping(): void {
    if (!this.selectedSourceRole || !this.selectedTargetRole) return;
    if (this.mappedRolesList.some((m: any) => m.source === this.selectedSourceRole)) {
      this.gtwSnackbarService.openSnackBar(`Same source multiple mapping is not allowed. [${this.selectedSourceRole}] is already added.`, 'gtw-snackbar--yellow');
      return;
    }
    this.mappedRolesList.push({ name: `${this.selectedSourceRole} - ${this.selectedTargetRole}`, source: this.selectedSourceRole, target: this.selectedTargetRole });
  }

  removeRoleMapping(index: number): void {
    this.mappedRolesList.splice(index, 1);
  }

  clearCopyAccess(): void {
    this.mappedRolesList = [];
    this.sourceRolesList = [];
    this.selectedSourceRole = '';
    this.sourceActivityName = '';
    this.selectedSourceActivityKey = null;
  }

  saveCopyAccess(): void {
    if (!this.mappedRolesList.length || !this.selectedSourceActivityKey || !this.selectedTargetActivityKey) return;
    this.saveMappingLoader = true;
    const mappedData = this.mappedRolesList.map((m: any) => ({ SOURCE_ROLE: m.source, TARGET_ROLE: m.target }));
    this.jdcManagerService.saveCopyAccess(
      mappedData, this.selectedSourceActivityKey, this.selectedTargetActivityKey,
      this.jcdKey, this.clientKey, this.globalParams?.tax_year, this.globalParams?.scenario
    ).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        this.saveMappingLoader = false;
        if (res?.callSuccess === '1') {
          this.gtwSnackbarService.openSnackBar('Copy roles access saved successfully.', 'gtw-snackbar--green');
          this.saved.emit();
        } else {
          this.gtwSnackbarService.openSnackBar('Problem saving mapping.', 'gtw-snackbar--red');
        }
        this.cdr.detectChanges();
      },
      error: () => {
        this.saveMappingLoader = false;
        this.gtwSnackbarService.openSnackBar('Problem saving mapping.', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      }
    });
  }

  private buildSourceTree(tree: any[]): any[] {
    const result: any[] = [];
    const walk = (nodes: any[]) => {
      (nodes || []).forEach((n: any) => {
        const activities = (n.children || []).filter(
          (a: any) => a?.activity_key !== this.selectedTargetActivityKey
        );
        if (activities.length > 0) {
          result.push({ task_name: n.task_name || n.name, activities });
        }
        walk(n.sub_task || []);
      });
    };
    walk(tree || []);
    return result;
  }

  private listAllActivities(tree: any[]): any[] {
    const list: any[] = [];
    const walk = (nodes: any[]) => {
      (nodes || []).forEach((n: any) => {
        (n.children || []).forEach((a: any) => list.push(a));
        walk(n.sub_task || []);
      });
    };
    walk(tree || []);
    return list;
  }
}
